import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import heroImageDefault from '@/assets/hero-image.jpg';
import logoDefault from '@/assets/logo.png';
import wordmark from '@/assets/Tyche-Palabra.png';

interface HeroProps {
  title?: string;
  subtitle?: string;
  body?: string;
  image?: string;
  logo?: string;
  ctaText?: string;
}

const renderTitleWithWordmark = (title: string) => {
  const parts = title.split('Tyche');
  if (parts.length === 1) return title;

  return parts.map((part, index) => (
    <span key={index}>
      {part}
      {index < parts.length - 1 && (
        <>
          <br />
          <img
            src={wordmark}
            alt="Tyche"
            className="inline-block h-16 md:h-20 lg:h-28 w-auto object-contain"
          />
        </>
      )}
    </span>
  ));
};

const Hero = ({ title, subtitle, body, image, logo, ctaText }: HeroProps) => {
  const scrollToProducts = () => {
    const element = document.getElementById('productos');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={image || heroImageDefault}
          alt="Perfumería y Joyería de Lujo"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 lg:px-8 pt-24">
        <div className="max-w-3xl">
          {/* Logo Badge */}
          <div className="mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
            <img
              src={logo || logoDefault}
              alt="Tyche Logo"
              className="h-32 w-32 lg:h-44 lg:w-44 object-contain"
            />
          </div>

          {/* Main Title */}
          <h1
            className="font-display text-4xl md:text-5xl lg:text-7xl font-semibold leading-tight mb-6 animate-fade-in"
            style={{ animationDelay: '0.2s' }}
          >
            <span className="text-gold-gradient">{renderTitleWithWordmark(title ?? 'Tyche')}</span>
            <span className="block text-foreground mt-2 text-3xl md:text-4xl lg:text-5xl font-normal">
              {subtitle ?? 'Perfumería y Joyería de Autor'}
            </span>
          </h1>

          {/* Subtitle */}
          <p
            className="font-body text-lg md:text-xl text-muted-foreground max-w-xl mb-10 leading-relaxed animate-fade-in"
            style={{ animationDelay: '0.4s' }}
          >
            {body ?? 'Donde el lujo encuentra la exclusividad. Descubre fragancias únicas y piezas de joyería que cuentan tu historia con elegancia atemporal.'}
          </p>

          {/* CTA Buttons */}
          <div
            className="flex flex-col sm:flex-row gap-4 animate-fade-in"
            style={{ animationDelay: '0.6s' }}
          >
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToProducts}
              className="group"
            >
              {ctaText ?? 'Ver colecciones destacadas'}
              <ChevronDown className="ml-2 h-5 w-5 transition-transform group-hover:translate-y-1" />
            </Button>
            <Button
              variant="goldOutline"
              size="xl"
              onClick={() => {
                const element = document.getElementById('contacto');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Contactar
            </Button>
          </div>

          {/* Decorative Line */}
          <div
            className="mt-16 line-gold w-32 animate-fade-in"
            style={{ animationDelay: '0.8s' }}
          />
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={scrollToProducts}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce cursor-pointer"
        aria-label="Scroll down"
      >
        <ChevronDown className="h-8 w-8 text-primary/60 hover:text-primary transition-colors" />
      </button>
    </section>
  );
};

export default Hero;
